import { Component, OnInit, Input, OnChanges } from '@angular/core';
import { CommonService } from 'src/app/shared/services/common.service';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import * as _ from 'underscore';
import { UsersService } from 'src/app/users/users.service';
import { SiteDashboardService } from '../site-dashboard/site-dashboard.service';
import { CommonDataService } from 'src/app/shared/services/common-data.service';
import { TableConfig } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { DecimalPipe } from '@angular/common';
import { DeviceFactory } from '../site-dashboard/device/device-factory/device-factory';
import { mainDeviceConfig } from '../site-dashboard/device/device-factory/device-config';
import { DeviceService } from '../site-dashboard/device/device.service';
import { IotahDevice } from '../site-dashboard/device/device-factory/Iotah-device';

@Component({
	selector: 'app-usage-data',
	templateUrl: './usage-data.component.html'
})
export class UsageDataComponent implements OnInit, OnChanges {
	@Input() deviceType?: number = DeviceFactory.types.iotah;
	@Input() data: any = [];
	@Input() columns: any = [];
	@Input() devices: any[];
	@Input() sites: any[];
	@Input() parentPage: string = 'device'; // options are: customer, site, device
	@Input() options: {hasTruckTypeFilter, hasYearFilter, hasStackedColumn, hasSiteFilter} = {hasTruckTypeFilter: false, hasYearFilter: false, hasStackedColumn: false, hasSiteFilter: false};
	@Input() tags: any[] = [];
	@Input() siteInfo: any = {};

	toSkipFields = [
		'data_avg_inuse_duration_seconds',
		'data_total_inuse_duration_seconds',
		'data_total_utilization_score',
		'data_avg_utilization_score',
		'data_total_utilization_energy_score',
		'data_avg_utilization_energy_score',
		'data_total_used_truck_days_count',
		'data_avg_used_truck_days_count',
		'data_total_idle_duration_seconds',
		'data_avg_idle_duration_seconds'
	];

	timeFields = [
		'data_avg_lift_time',
		'data_total_lift_time',
		'data_avg_travel_time',
		'data_total_travel_time'
	]

	deviceConfig: mainDeviceConfig | null = (new IotahDevice).config;
	selectedYear = moment().utc().get('year');
	selectedTruckType = '';
	selectedTruckYear = '';
	selectedSites = [];
	selectedTags = [];

	exportFileName = '';

	selectedTagNames = [];

	ddList = {
		truckTypes: [],
		truckYearsList: [],
	};
	recentData: any = [];
	recentColumns: any = [];
	recentFileName: string = '';

	hasData = false;

	tooltips = '';

	truckTypesMapper = {};

	readonly chargLinkTruckType = 'chargLink';

	tableData = [];
	tableColumns = [];
	tableConfig: TableConfig = {
		hasExport: true,
		hasPagination: false,
		fileName: '',
		draggable: true,
		csvFileHeaderInfo: []
	};

	constructor(
		private translate: TranslateService,
		public common: CommonService,
		public commonData: CommonDataService,
		private userService: UsersService,
		private siteDashboardService: SiteDashboardService,
		private deviceService: DeviceService,
	){}

	ngOnInit() {
		this.data = this.data.slice(1);
		this.hasData = !!this.data.length;
		this.deviceConfig = DeviceFactory.createDevice(this.deviceType).config;

		this.tooltips = this.common.getTooltipKey('recent_data_'+this.parentPage, this.userService.userHasNOCAccess());
		this.tooltips = this.tooltips ? this.deviceService.getTranslationKey(this.tooltips, this.deviceType) : this.tooltips;

		this.ddList.truckYearsList = [];
		for(let item of ['all', 'new', 'old']) {
			this.ddList.truckYearsList.push({
				value: item == 'all' ? '' : item,
				label: this.translate.instant('site_performance.truck_year_'+item)
			});
		}

		this.formatDataAndColumns(this.data, this.columns);
	}

	ngOnChanges(changes) {
		this.deviceConfig = DeviceFactory.createDevice(this.deviceType).config;
		if(!changes.data?.firstChange) {
			this.data = this.data.slice(1);
			this.formatDataAndColumns(this.data, this.columns);
		}

		this.hasData = !!this.data.length;

		this.tooltips = this.common.getTooltipKey('recent_data_'+this.parentPage, this.userService.userHasNOCAccess());
		this.tooltips = this.tooltips ? this.deviceService.getTranslationKey(this.tooltips, this.deviceType) : this.tooltips;

		if(this.parentPage == 'device')
			this.exportFileName = `${this.siteInfo.name} - ${this.devices[0].config_info.truck_id} - ${this.deviceConfig.typeString} Usage Data`;
		else {
			this.exportFileName = `${this.siteInfo.name} - ${this.deviceConfig.typeString} Usage Data`;
			let truckTypesOptions: any = [
				{id: '', text: this.deviceService.translate('site_performance.all_truck_types', this.deviceType)}
			];

			_.uniq(_.pluck(this.devices, 'truck_type')).forEach((truckType, idx) => {
				this.truckTypesMapper[truckType || '%'] = truckType;

				if (this.deviceConfig.hasTypes || this.parentPage == 'device') {
					const record: {id: number, text: string} = {id: truckType == '' ? '%' : truckType, text: truckType};
					truckTypesOptions.push(record);
				}
			});

			this.ddList.truckTypes = truckTypesOptions;

			this.recentFileName = this.deviceService.translate('perf_analytic.recent_data', this.deviceType);
		}
		this.tableConfig.fileName = this.exportFileName;
		this.tableConfig.csvFileHeaderInfo = this.common.getCsvFileHeadersInfo({
			customerName: this.siteInfo.customer_name,
			siteName: this.siteInfo.name,
			deviceSN: this.parentPage == 'page' ? this.devices[0].config_info.serial_number : null,
			includeSpacing: false
		});
	}

	onSelectedTagsChanges(event) {
		this.selectedTags = event;

		this.selectedTagNames = [];
		for(let tagId of event) {
			let tag = _.findWhere(this.tags, {id: tagId})
			if(tag)
				this.selectedTagNames.push(tag.tag_name)
		}
		this.buildSiteCharts();
	}

	private buildSiteCharts() {
		this.tableData = [];
		const ids = this.getUsageIds();
		this.siteDashboardService.getTruckUsageData(ids, 'recent', {truckYear: this.selectedTruckYear, truckType: this.truckTypesMapper[this.selectedTruckType], truckTag: this.selectedTags, parentPage: this.parentPage}).subscribe((res: any) => {
			const dataType = this.deviceConfig.reporting.device;

			this.data = res[dataType].slice(1);
			this.columns = res[dataType][0] || [];

			this.formatDataAndColumns(this.data, this.columns);
		});
	}

	getUsageIds() {
		let ids;
		switch (this.parentPage) {
			case 'customer':
				ids = (this.selectedSites.length ? this.selectedSites : this.sites.map((item) => item.id));
			break;
			case 'site':
				ids = this.siteInfo.id;
			break;
			case 'device':
				ids = this.devices[0].mac_address;
			break;
		}
		return ids;
	}

	rebuildPerformanceCharts() {
		return this.buildSiteCharts();
	}

	formatDataAndColumns(data: any, columns: any) {
		this.tableColumns = [];
		const tableData = [];
		const filterParams = {};

		for (const col of columns) {
			if (this.toSkipFields.some(skipField => col.field.includes(skipField)))
				continue;

			if (col.filterParams?.format)
				filterParams[col.field] = col.filterParams?.format;

			this.tableColumns.push({ key: col.field, name: this.translate.instant(col.headerName), type: col.type || 'string' });
		}

		for (const row of data) {
			const rawData = {};
			for (const key in row) {
				let displayValue = row[key];
				if (key != 'period')
					displayValue = Math.round(row[key] || 0).toLocaleString();

				if (this.timeFields.some(timeFiled => key.includes(timeFiled)))
					displayValue = Math.round((row[key] || 0) / 3600).toLocaleString();

				rawData[key] = { value: row[key], displayValue };
			}

			tableData.push(rawData);
		}

		this.tableData = [...tableData];
	}
}
